/**
 * Unificación de página web y redes sociales para empresas.
 * Fuente oficial al escribir: web + redes_sociales (+ contactoAdicional para compat).
 * Lectura: siempre a través de estos getters para soportar alias antiguos.
 */

const EMPTY = '';

function trim(v) {
  if (v == null) return EMPTY;
  return String(v).trim();
}

/** True si hay algo que mostrar como enlace */
export function hasContactValue(value) {
  return trim(value) !== EMPTY;
}

/**
 * Página web única — alias soportados al leer.
 */
export function getPaginaWeb(empresa) {
  if (!empresa || typeof empresa !== 'object') return EMPTY;
  const v =
    empresa.web ??
    empresa.web_actual ??
    empresa.sitioWeb ??
    empresa.paginaWeb ??
    empresa.perfilWeb ??
    empresa.web_empresa ??
    EMPTY;
  return trim(v);
}

/** Admin: la URL existe pero se marcó como caída / no funciona (solo referencia). */
export function readPaginaWebNoFunciona(empresa) {
  if (!empresa || typeof empresa !== 'object') return false;
  return empresa.paginaWebNoFunciona === true || empresa.webNoFunciona === true;
}

/**
 * URL para perfil público: vacía si el admin marcó "Página no funciona" (no mostrar enlace).
 */
export function getPaginaWebPerfilPublico(empresa) {
  if (readPaginaWebNoFunciona(empresa)) return EMPTY;
  return getPaginaWeb(empresa);
}

/**
 * Redes unificadas (facebook, instagram, tiktok, whatsapp).
 * Combina redes_sociales, contactoAdicional y campos planos legacy.
 */
export function getRedesSocialesUnificadas(empresa) {
  if (!empresa || typeof empresa !== 'object') {
    return { facebook: EMPTY, instagram: EMPTY, tiktok: EMPTY, whatsapp: EMPTY };
  }
  const rs = empresa.redes_sociales || {};
  const ca = empresa.contactoAdicional || {};
  return {
    facebook: trim(rs.facebook || ca.facebook || empresa.facebook || EMPTY),
    instagram: trim(rs.instagram || ca.instagram || empresa.instagram || EMPTY),
    tiktok: trim(rs.tiktok || ca.tiktok || empresa.tiktok || EMPTY),
    whatsapp: trim(rs.whatsapp || ca.whatsapp || EMPTY)
  };
}

/**
 * Agrega https:// si falta protocolo; no rompe si ya es mailto/tel/javascript
 */
export function normalizeExternalUrl(value, type = 'web') {
  const s = trim(value);
  if (!s) return EMPTY;
  if (/^(https?:|mailto:|tel:|\/)/i.test(s)) return s;
  const lower = s.toLowerCase();
  if (type === 'instagram') {
    if (lower.includes('instagram.com')) return lower.startsWith('http') ? s : `https://${s.replace(/^\/\//, '')}`;
    const handle = s.replace(/^@/, '').replace(/^\//, '');
    if (handle && !handle.includes('.')) return `https://instagram.com/${handle}`;
  }
  if (type === 'facebook') {
    if (lower.includes('facebook.com') || lower.includes('fb.com')) return lower.startsWith('http') ? s : `https://${s.replace(/^\/\//, '')}`;
    const handle = s.replace(/^@/, '');
    if (handle && !handle.includes('.')) return `https://facebook.com/${handle}`;
  }
  if (type === 'tiktok') {
    if (lower.includes('tiktok.com')) return lower.startsWith('http') ? s : `https://${s.replace(/^\/\//, '')}`;
    const handle = s.replace(/^@/, '');
    if (handle) return `https://www.tiktok.com/@${handle.replace(/^@/, '')}`;
  }
  if (lower.startsWith('www.')) return `https://${s}`;
  return `https://${s}`;
}

/**
 * Payload para guardar en Firestore manteniendo compatibilidad.
 * Escribe web + redes_sociales + contactoAdicional (facebook/instagram/tiktok/whatsapp).
 */
export function buildWebRedesWritePayload({ paginaWeb, facebook, instagram, tiktok, whatsapp, contactoAdicionalPrev = {}, empresaPrev = {} }) {
  const web = trim(paginaWeb);
  const fb = trim(facebook);
  const ig = trim(instagram);
  const tt = trim(tiktok);
  const wa = trim(whatsapp);

  const prevRs = (empresaPrev && empresaPrev.redes_sociales) || {};
  const redes_sociales = {
    ...prevRs,
    facebook: fb,
    instagram: ig,
    tiktok: tt,
    whatsapp: wa || undefined
  };
  // Eliminar vacíos para no pisar con ''
  Object.keys(redes_sociales).forEach((k) => {
    if (redes_sociales[k] === EMPTY || redes_sociales[k] == null) delete redes_sociales[k];
  });

  const contactoAdicional = {
    ...(typeof contactoAdicionalPrev === 'object' ? contactoAdicionalPrev : {}),
    facebook: fb || contactoAdicionalPrev.facebook || EMPTY,
    instagram: ig || contactoAdicionalPrev.instagram || EMPTY,
    tiktok: tt || contactoAdicionalPrev.tiktok || EMPTY,
    whatsapp: wa || contactoAdicionalPrev.whatsapp || EMPTY
  };
  Object.keys(contactoAdicional).forEach((k) => {
    if (contactoAdicional[k] === EMPTY) delete contactoAdicional[k];
  });

  return {
    web: web || null,
    web_actual: web || null,
    redes_sociales,
    contactoAdicional
  };
}

/**
 * Solo presencia digital (web + facebook/instagram/tiktok).
 * No modifica whatsapp ni direccionCompleta en contactoAdicional; no borra claves legacy en redes_sociales salvo las tres redes.
 */
export function buildWebRedesDigitalOnlyPayload({
  paginaWeb,
  facebook,
  instagram,
  tiktok,
  paginaWebNoFunciona,
  contactoAdicionalPrev = {},
  empresaPrev = {}
}) {
  const web = trim(paginaWeb);
  const fb = trim(facebook);
  const ig = trim(instagram);
  const tt = trim(tiktok);
  const prevRs = (empresaPrev && empresaPrev.redes_sociales) || {};
  const redes_sociales = { ...prevRs };
  if (fb !== EMPTY) redes_sociales.facebook = fb;
  else delete redes_sociales.facebook;
  if (ig !== EMPTY) redes_sociales.instagram = ig;
  else delete redes_sociales.instagram;
  if (tt !== EMPTY) redes_sociales.tiktok = tt;
  else delete redes_sociales.tiktok;

  const prevCa = typeof contactoAdicionalPrev === 'object' ? { ...contactoAdicionalPrev } : {};
  if (fb !== EMPTY) prevCa.facebook = fb;
  else delete prevCa.facebook;
  if (ig !== EMPTY) prevCa.instagram = ig;
  else delete prevCa.instagram;
  if (tt !== EMPTY) prevCa.tiktok = tt;
  else delete prevCa.tiktok;

  const out = {
    web: web || null,
    web_actual: web || null,
    redes_sociales,
    contactoAdicional: prevCa
  };
  if (paginaWebNoFunciona !== undefined) {
    out.paginaWebNoFunciona = paginaWebNoFunciona === true;
  }
  return out;
}
